import { useCallback, useEffect, useRef, useState } from 'react';

// Private assets are served as signed URLs that expire, so anything holding them has to be
// re-fetched periodically and whenever the user returns to a tab that was left open.
export const SIGNED_URL_REFRESH_MS = 10 * 60 * 1000;

export function useSignedResource<T>(
    resourceId: string | undefined,
    fetcher: (resourceId: string) => Promise<T>,
    refreshAfterMs: number = SIGNED_URL_REFRESH_MS,
) {
    const [data, setData] = useState<T | null>(null);
    const [loading, setLoading] = useState(true);

    const fetcherRef = useRef(fetcher);
    fetcherRef.current = fetcher;

    const activeRef = useRef(true);
    const fetchedAtRef = useRef(0);

    const load = useCallback(async (isInitial: boolean) => {
        if (!resourceId) {
            setData(null);
            setLoading(false);
            return;
        }
        if (isInitial) setLoading(true);
        try {
            const result = await fetcherRef.current(resourceId);
            if (!activeRef.current) return;
            setData(result);
            fetchedAtRef.current = Date.now();
        } catch {
            // A failed background refresh keeps the previous URLs rather than blanking the view.
            if (activeRef.current && isInitial) setData(null);
        } finally {
            if (activeRef.current && isInitial) setLoading(false);
        }
    }, [resourceId]);

    useEffect(() => {
        activeRef.current = true;
        fetchedAtRef.current = 0;
        load(true);
        return () => { activeRef.current = false; };
    }, [load]);

    useEffect(() => {
        if (!resourceId) return;
        const refreshIfStale = () => {
            if (document.visibilityState !== 'visible') return;
            if (Date.now() - fetchedAtRef.current < refreshAfterMs) return;
            load(false);
        };
        const interval = window.setInterval(refreshIfStale, refreshAfterMs);
        document.addEventListener('visibilitychange', refreshIfStale);
        window.addEventListener('focus', refreshIfStale);
        return () => {
            window.clearInterval(interval);
            document.removeEventListener('visibilitychange', refreshIfStale);
            window.removeEventListener('focus', refreshIfStale);
        };
    }, [resourceId, refreshAfterMs, load]);

    return { data, loading };
}
